/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import _ from 'lodash';

import { CustomRole, Role } from '../../../../ts';
import type { Environment } from '../../../../ts/assistant';
import { FeatureFlag } from '../../../../ts/feature-flags';
import type { Skill } from '../../../../ts/skill';
import type { RootState } from '../../../../ts/state';
import { isWxoEnabled } from '../../util/config';
import { selectIsLogReaderForAssistant } from '../assistants/selectors';
import { selectAllEnvironments } from '../environments/selectors';
import {
  isFeatureEnabled,
  selectIsAccessUpdatesEnabled
} from '../features/selectors';
import {
  selectInstanceCustomRoles,
  selectIsNewIa
} from '../instance/selectors';
import { selectIsReleaseInProgress } from '../releases/core-selectors';
import { getAssistantId } from '../session/selectors';
import { selectAllSkills } from '../skills/core-selectors';

type AccessControlByResource = {
  accessControlStatus: boolean;
  role?: Role;
};

export const selectAccessControlBySkillId = (
  state: RootState,
  skillId: string
): AccessControlByResource => {
  const isSkillBlocked = selectIsReleaseInProgress(state);

  return {
    accessControlStatus: state.accessControl.accessControlStatus,
    role: isSkillBlocked
      ? Role.READER
      : _.get(state, `accessControl.skills[${skillId}]`)
  };
};

export const selectAccessControlByAssistantId = (
  state: RootState,
  assistantId: string
): AccessControlByResource => {
  return {
    accessControlStatus: state.accessControl.accessControlStatus,
    role: _.get(state, `accessControl.assistants[${assistantId}]`)
  };
};

export const selectAccessControlByEnvironmentId = (
  state: RootState,
  environmentId: string
): AccessControlByResource => {
  return {
    accessControlStatus: state.accessControl.accessControlStatus,
    role: _.get(state, `accessControl.environments[${environmentId}]`)
  };
};

export const selectInstanceAccessControl = (
  state: RootState
): AccessControlByResource => {
  return {
    accessControlStatus: state.accessControl?.accessControlStatus || false,
    role: state.accessControl?.instance || undefined
  };
};

export const iaUpcomingMaintenance = state =>
  isFeatureEnabled(state, FeatureFlag.IA_UPCOMING_MAINTENANCE);

export const iaOngoingMaintenanceReadOnly = state =>
  isFeatureEnabled(state, FeatureFlag.IA_ONGOING_MAINTENANCE);
/**
 * Check if the instance should turn on read-only mode.
 * @param {object} state the redux store state
 * @returns {boolean} returns true if it should be read-only
 */
export const isInstanceReadOnly = (state: RootState): boolean => {
  if (iaOngoingMaintenanceReadOnly(state)) {
    return true;
  }
  if (!isAccessControlOn(state)) {
    return false;
  }
  const instanceContext = selectInstanceAccessControl(state);
  return !hasRole(instanceContext) || instanceContext.role === Role.READER;
};

export const selectIsSkillReadOnly = (state: RootState): boolean => {
  const skillId = state.session?.skillId;
  if (skillId) {
    return isSkillReadOnly(state, skillId);
  }
  return false;
};
/**
 * Check if a skill should turn on read-only mode.
 * @param {object} state the redux store state
 * @param {string} skillId the id of the skill
 * @returns {boolean} returns true if it should be read-only
 */
export const isSkillReadOnly = (state: RootState, skillId: string): boolean => {
  if (iaOngoingMaintenanceReadOnly(state)) {
    return true;
  }

  if (!isAccessControlOn(state)) {
    return false;
  }
  const skillContext = selectAccessControlBySkillId(state, skillId);
  // TEMP: the accessUpdates flag will be removed when used/true universally
  if (selectIsAccessUpdatesEnabled(state)) {
    // user is skill.writer if assistant.writer
    if (hasRole(skillContext) && skillContext.role !== Role.READER) {
      return false;
    }
    const assistantId = getAssistantId(state);
    return isAssistantReadOnly(state, assistantId);
  } else {
    if (hasRole(skillContext)) {
      return skillContext.role === Role.READER;
    }
    return isInstanceReadOnly(state);
  }
};

/**
 * Check if an assistant should turn on read-only mode.
 * @param {object} state the redux store state
 * @param {string} assistantId the id of the assistant
 * @returns {boolean} returns true if it should be read-only
 */
export const isAssistantReadOnly = (
  state: RootState,
  assistantId: string
): boolean => {
  if (iaOngoingMaintenanceReadOnly(state)) {
    return true;
  }

  if (!isAccessControlOn(state)) {
    return false;
  }
  const assistantContext = selectAccessControlByAssistantId(state, assistantId);
  if (hasRole(assistantContext)) {
    return assistantContext.role === Role.READER;
  }
  return isInstanceReadOnly(state);
};

/**
 * Check if an environment is read-only to the current user
 * @param {object} state the redux store state
 * @param {string} environmentId the id of the assistant
 * @returns {boolean} returns true if it should be read-only
 */
export const isEnvironmentReadOnly = (
  state: RootState,
  environmentId: string
): boolean => {
  if (iaOngoingMaintenanceReadOnly(state)) {
    return true;
  }

  if (!isAccessControlOn(state)) {
    return false;
  }
  const environmentContext = selectAccessControlByEnvironmentId(
    state,
    environmentId
  );
  if (selectIsNewIa(state)) {
    return (
      !hasRole(environmentContext) || environmentContext.role === Role.READER
    );
  } else {
    // Classic has no Environments - falls back to Assistant role
    const assistantId = getAssistantId(state);
    return isAssistantReadOnly(state, assistantId);
  }
};

/**
 * Check if either the assistant or the instance is read-only
 * @param {object} state the redux store state
 * @param {string} assistantId the id of the assistant
 * @returns {boolean} returns true if either assistant or instance are read-only
 */
export const isAssistantOrInstanceReadOnly = (
  state: RootState,
  assistantId: string
): boolean => {
  return (
    isAssistantReadOnly(state, assistantId) ||
    isInstanceReadOnly(state) ||
    iaOngoingMaintenanceReadOnly(state)
  );
};

/**
 * Check if the user has manager role to access a skill
 * @param {object} state the redux store state
 * @param {string} skillId an access control context
 * @returns {boolean} returns true if the role is manager role or access control is not enabled
 */
export const isManagerOfSkill = (
  state: RootState,
  skillId: string
): boolean => {
  if (!isAccessControlOn(state)) {
    return true;
  }
  const skillContext = selectAccessControlBySkillId(state, skillId);
  const instanceContext = selectInstanceAccessControl(state);
  if (hasRole(skillContext)) {
    return skillContext.role === Role.MANAGER;
  }
  return instanceContext.role === Role.MANAGER;
};

/**
 * Check if the user has manager role to access an assistant
 * @param {object} state the redux store state
 * @param {string} assistantId an access control context
 * @returns {boolean} returns true if the role is manager role or access control is not enabled
 */
export const isManagerOfAssistant = (
  state: RootState,
  assistantId: string
): boolean => {
  if (!isAccessControlOn(state)) {
    return true;
  }
  const assistantContext = selectAccessControlByAssistantId(state, assistantId);
  const instanceContext = selectInstanceAccessControl(state);
  if (hasRole(assistantContext)) {
    return assistantContext.role === Role.MANAGER;
  }
  return instanceContext.role === Role.MANAGER;
};

/**
 * Check if the user has writer (or higher) role to access an assistant
 * @param {object} state the redux store state
 * @param {string} assistantId an access control context
 * @returns {boolean} returns true if the role is writer (or higher) role or
 * access control is not enabled
 */
export const isWriterOfAssistant = (
  state: RootState,
  assistantId: string
): boolean => {
  if (iaOngoingMaintenanceReadOnly(state)) {
    return false;
  }
  if (isManagerOfAssistant(state, assistantId) || !isAccessControlOn(state)) {
    return true;
  }
  const assistantContext = selectAccessControlByAssistantId(state, assistantId);
  const instanceContext = selectInstanceAccessControl(state);
  if (hasRole(assistantContext)) {
    return assistantContext.role === Role.WRITER;
  }
  return instanceContext.role === Role.WRITER;
};

export const isWriterOfInstance = (state: RootState): boolean => {
  if (iaOngoingMaintenanceReadOnly(state)) {
    return false;
  }
  if (isManagerOfInstance(state) || !isAccessControlOn(state)) {
    return true;
  }
  const instanceContext = selectInstanceAccessControl(state);
  if (hasRole(instanceContext)) {
    return instanceContext.role === Role.WRITER;
  }
};

/**
 * Check if the user owns the instance
 * @param {Object} state the redux store state
 * @returns {boolean} returns true if the owner email matches the user email
 * (i.e. the instance belongs to the current user)
 */
export const isOwnerOfInstance = (state: RootState): boolean => {
  const account = state?.session?.account;
  const user = state?.session?.user;
  return account?.owner_userid === user?.email;
};

/**
 * Check if the user has manager role to access the instance
 * @param {object} state the redux store state
 * @returns {boolean} returns true if the role is manager role or access control is not enabled
 */
export const isManagerOfInstance = (state: RootState): boolean => {
  if (!isAccessControlOn(state)) {
    return true;
  }
  const instanceContext = selectInstanceAccessControl(state);
  return instanceContext.role === Role.MANAGER;
};

/**
 * Check if the access control is on.
 * @param {Object} state the redux store state
 * @returns {boolean} returns true if accessControlStatus is true
 */
export const isAccessControlOn = (state: RootState): boolean =>
  isFeatureEnabled(state, FeatureFlag.ACCESS_CONTROL) &&
  state.accessControl.accessControlStatus;

export const hasRole = (context?: AccessControlByResource) =>
  typeof context?.role === 'string';

/**
 * Check if the user can see an assistant
 * @param {Object} state the redux store state
 * @param {string} assistantId the id of the target Assistant
 * @returns {boolean} returns true if the user has access to the assistant
 */
export const hasAccessToAssistant = (
  state: RootState,
  assistantId: string
): boolean => {
  if (!isAccessControlOn(state)) {
    return true;
  }
  const assistantContext = selectAccessControlByAssistantId(state, assistantId);
  const instanceContext = selectInstanceAccessControl(state);
  if (
    !hasRole(assistantContext) &&
    (!hasRole(instanceContext) || instanceContext.role === Role.CREATOR)
  ) {
    return false;
  }
  return true;
};

export const hasAccessToSkill = (
  state: RootState,
  skillId: string
): boolean => {
  if (!isAccessControlOn(state)) {
    return true;
  }
  const skillContext = selectAccessControlBySkillId(state, skillId);
  const instanceContext = selectInstanceAccessControl(state);
  if (
    !hasRole(skillContext) &&
    (!hasRole(instanceContext) || instanceContext.role === Role.CREATOR)
  ) {
    return false;
  }
  return true;
};

export const getAssistantRole = (
  state: RootState,
  assistantId: string
): Role | undefined => state.accessControl.assistants[assistantId];

export const getSkillRole = (
  state: RootState,
  skillId: string
): Role | undefined => state.accessControl.skills[skillId];

export const getInstanceRole = (state: RootState): Role | undefined =>
  state.accessControl.instance;

/**
 * Returns true if at least one environment has read only access
 * @param {*} state The redux state
 * @returns {Boolean} true if its a reader in at least one environment
 */
export const selectIsEnvironmentsReadOnly = (state: RootState): boolean => {
  const environments = selectAllEnvironments(state);
  const isReadOnly = environments.some(e =>
    isEnvironmentReadOnly(state, e.environment_id)
  );
  return isReadOnly;
};

/**
 * Returns skills with readOnly flag
 * @param {*} state The redux state
 * @returns {Array} array of all skills with readOnly info
 */
export const selectAreSkillsReadOnly = (state: RootState): Skill[] => {
  const skills = selectAllSkills(state);
  return skills.filter(s => isSkillReadOnly(state, s.skill_id));
};

/**
 * Returns environments with readOnly flag
 * @param {*} state The redux state
 * @returns {Array} array of all environments with readOnly info
 */
export const selectAreEnvironmentsReadOnly = (
  state: RootState
): Environment[] => {
  const envs = selectAllEnvironments(state);
  const accessControlStatus = state?.accessControl?.accessControlStatus;
  const assistants = state?.accessControl?.assistants;
  return envs.filter(
    e => accessControlStatus && assistants[e.assistant_id] === Role.READER
  );
};

export const selectIsInstanceLogsReader = (state: RootState): boolean => {
  if (isManagerOfInstance(state)) {
    return true;
  }

  if (isWxoEnabled && isWriterOfInstance(state)) {
    return true;
  }
  const customRoles = selectInstanceCustomRoles(state);
  return customRoles?.some(
    entry =>
      entry === CustomRole.LOGS_READER || entry === CustomRole.LOGS_READER_DEV
  );
};

export const selectHasAccessToAnalytics = (
  state: RootState,
  assistantId: string
): boolean => {
  if (selectIsInstanceLogsReader(state)) {
    return true;
  }

  return selectIsLogReaderForAssistant(state, assistantId);
};
